<script setup lang="ts">
import { useRoute } from 'vue-router';
import ActionButton from '@/components/buttons/ActionButton.vue';
import Avatar from '@/features/avatar/Avatar.vue';
import LanguageSelector from '@/features/mobile/drawer/LanguageSelector.vue';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { useNavigation } from '@/hooks/useNavigation';
import { useSpaces } from '@/hooks/useSpaces';
import { burgerNavItems, logout as logoutNavItem } from '@/router/navigation';
import { useGlobalStore } from '@/stores/global';
import { useLocaleStore } from '@/stores/locales';

const globalStore = useGlobalStore();
const { logout } = globalStore;
const localeStore = useLocaleStore();

const { auth, fullName } = useMeQuery();

const isActive = (route) => {
  return !!useRoute().matched.filter((item) => item.path === route).length;
};

const { availableItems } = useNavigation(burgerNavItems);
const { hasSpaces, redirectToSpaces } = useSpaces();
</script>

<template>
  <v-navigation-drawer
    :location="localeStore.isRtl ? 'right' : 'left'"
    disable-resize-watcher
    v-model="globalStore.drawer"
    touchless
    app
  >
    <template v-slot:prepend>
      <div class="flex items-center p-3">
        <Avatar img-size="thumb" :size="50" :branding="auth?.branding" class="me-2" />
        <div class="flex flex-col justify-center">
          <span class="text-subtitle-3 mb-1">{{ fullName }}</span>
          <span class="text-caption font-light">{{ auth?.accurate_job_title }}</span>
        </div>
      </div>
      <v-divider></v-divider>
    </template>

    <v-list nav class="p-0">
      <!-- SPACES BLOCK MENU-->
      <v-list-item v-if="hasSpaces" class="m-0 p-0">
        <div class="flex items-end justify-between gap-12 p-4">
          <p :class="`text-subtitle-3 font-bold text-primary`" data-cy="candiate-spaces-menu-role">
            {{ $t('general.role.personal') }}
          </p>
          <ActionButton
            cancel
            class="h-auto"
            data-cy="candidate-spaces-menu-btn"
            @click="redirectToSpaces"
          >
            {{ $t('navigation.go_to.space') }}
          </ActionButton>
        </div>
      </v-list-item>

      <v-divider v-if="hasSpaces"></v-divider>
      <!-- SPACES BLOCK MENU-->
      <v-list-item
        v-for="item in availableItems"
        :key="item.name"
        :value="item"
        color="contrast"
        :disabled="!item.route"
        :active="isActive(item.route)"
        :href="item.external ? item.route : ''"
        :to="!item.external ? item.route : ''"
        class="ps-5 mb-0"
      >
        <template v-slot:prepend>
          <v-icon :icon="item.icon" class="text-icon-3"></v-icon>
        </template>

        <span class="text-body-3">{{ $t(item.label) }}</span>
      </v-list-item>

      <LanguageSelector />

      <v-divider></v-divider>

      <v-list-item color="contrast" @click="logout(200)" class="ps-5 mb-0">
        <template v-slot:prepend>
          <v-icon :icon="logoutNavItem.icon" class="text-icon-3"></v-icon>
        </template>
        <span class="text-body-3">{{ $t(logoutNavItem.label) }}</span>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>
